import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Grid,
    Tooltip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    BusinessCenterOutlined as BusinessCenterIcon,
    PeopleAltOutlined as PeopleAltIcon,
    DescriptionOutlined as DescriptionOutlinedIcon,
    TableView as TableViewIcon,
    StorageOutlined as StorageIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    ErrorOutlineRounded as ErrorOutlineIcon,
    HighlightOffOutlined as HighlightOffOutlinedIcon,
    Flag as FlagIcon,
    InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import SheetErrorsDialog from './SheetErrorsDialog';

/**
 * Component for displaying a single data source card
 */
const DataSourceCard = ({
    source,
    onTest,
    onEdit,
    onDelete,
    onView,
    connectingSourceIds,
    currentUserId
}) => {
    // Check if current user can modify this connection
    // Only the creator can modify (edit/delete) a connection
    const canModify = source.created_by === currentUserId;
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);

    // Texto rojo clickeable que abre el detalle de por qué falló (por hoja,
    // ver SheetErrorsDialog).
    const errorLinkSx = {
        color: 'var(--wb-danger-strong)',
        cursor: 'pointer',
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        '&:hover': { textDecorationStyle: 'solid' }
    };
    const openErrorDialog = () => setErrorDialogOpen(true);
    const onErrorKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openErrorDialog();
        }
    };
    const errorLinkProps = {
        role: 'button',
        tabIndex: 0,
        onClick: openErrorDialog,
        onKeyDown: onErrorKeyDown,
        title: 'Ver detalle del error'
    };

    // Función para renderizar el ícono según el tipo
    const renderTypeIcon = (type) => {
        switch (type) {
            case 'ERP':
                return <BusinessCenterIcon sx={{ color: 'var(--wb-x-text-ff5926, #ff5926ff)' }} />;
            case 'CRM':
                return <PeopleAltIcon sx={{ color: 'var(--wb-primary)' }} />;
            case 'SQL':
                return <StorageIcon sx={{ color: 'var(--wb-warning-strong)' }} />;
            case 'Excel':
                return <DescriptionOutlinedIcon sx={{ color: 'var(--wb-x-text-0ab901, #0ab901ff)' }} />;
            default:
                return <TableViewIcon sx={{ color: 'var(--wb-primary)' }} />;
        }
    };

    // Función para renderizar el estado
    const renderStatus = (source) => {
        // Si la fuente está en proceso de conexión, mostrar el estado "connecting"
        if (connectingSourceIds.includes(source.id)) {
            return (
                <>
                    <CircularProgress size={16} sx={{ mr: 0.5, color: 'var(--wb-primary)' }} />
                    <Typography variant="body2" sx={{ color: 'var(--wb-primary)' }}>Conectando...</Typography>
                </>
            );
        }

        // Si no está conectando, mostrar el estado normal
        switch (source.status) {
            case 'connected':
                return (
                    <>
                        <CheckCircleOutlineIcon sx={{ color: 'var(--wb-x-text-58c55b, #58c55bff)', mr: 0.5, width: 16 }} />
                        <Typography variant="body2" sx={{ color: 'var(--wb-x-text-58c55b, #58c55bff)' }}>Conectado</Typography>
                    </>
                );
            case 'error': {
                const userMessage = source.errorMessageUser || 'No se pudo procesar la conexión.';
                return (
                    <>
                        <HighlightOffOutlinedIcon sx={{ color: 'var(--wb-danger-strong)', mr: 0.5, width: 16 }} />
                        <Typography variant="body2" sx={errorLinkSx} {...errorLinkProps}>
                            {userMessage}
                        </Typography>
                        {source.errorMessage && (
                            <Tooltip
                                title={source.errorMessage}
                                arrow
                                placement="top"
                            >
                                <InfoOutlinedIcon
                                    sx={{
                                        ml: 0.5,
                                        color: 'var(--wb-danger-strong)',
                                        fontSize: '1.1rem',
                                        cursor: 'help'
                                    }}
                                />
                            </Tooltip>
                        )}
                    </>
                );
            }
            case 'pending':
                return (
                    <>
                        <ErrorOutlineIcon sx={{ color: 'var(--wb-warning-strong)', mr: 0.5, width: 16 }} />
                        <Typography variant="body2" sx={{ color: 'var(--wb-warning-strong)' }}>Pendiente</Typography>
                    </>
                );
            case 'processing':
            case 'ingesting': {
                // Ingesta multi-hoja: si hay contadores disponibles (del
                // listado, o del polling en memoria mientras esta pantalla
                // sigue abierta -- ver SemanticConnections.jsx::pollInBackground)
                // se muestra "X/Y hojas"; si no, un spinner genérico.
                const { totalSheets, sheetsCompleted, sheetsFailed } = source;
                const hasSheetProgress = totalSheets != null && totalSheets > 1;
                const label = hasSheetProgress
                    ? `Procesando ${sheetsCompleted || 0}/${totalSheets} hojas...`
                    : 'Procesando...';
                return (
                    <>
                        <CircularProgress size={16} sx={{ mr: 0.5, color: 'var(--wb-primary)' }} />
                        <Typography variant="body2" sx={{ color: 'var(--wb-primary)' }}>{label}</Typography>
                        {hasSheetProgress && sheetsFailed > 0 && (
                            <Typography variant="body2" sx={{ ...errorLinkSx, ml: 0.75 }} {...errorLinkProps}>
                                {sheetsFailed === 1 ? '1 hoja con error' : `${sheetsFailed} hojas con error`}
                            </Typography>
                        )}
                    </>
                );
            }
            default:
                return null;
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                bgcolor: 'var(--wb-bg-card)',
                p: 2,
                borderRadius: 2,
                mb: 2,
                border: '1px solid var(--wb-overlay-10)'
            }}
        >
            <Grid container spacing={2} alignItems="center">
                <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                    {renderTypeIcon(source.type)}
                </Grid>
                <Grid item xs>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant="subtitle1" sx={{ mr: 1 }} color="var(--wb-text-strong)">
                            {source.name}
                        </Typography>
                        <Box
                            sx={{
                                bgcolor: 'var(--wb-x-bg-333d4d, #333d4d)',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.7rem'
                            }}
                        >
                            <Typography variant="caption" color="var(--wb-text-strong)">
                                {source.type}
                            </Typography>
                        </Box>
                        {source.isPublic && (
                            <Tooltip 
                                title="Conexión pública: Visible para todos en tu empresa. Solo el creador puede modificarla o eliminarla."
                                arrow
                                placement="top"
                            >
                                <FlagIcon 
                                    sx={{ 
                                        ml: 1, 
                                        color: 'var(--wb-warning-strong)',
                                        fontSize: '1.2rem',
                                        cursor: 'help'
                                    }} 
                                />
                            </Tooltip>
                        )}
                    </Box>
                    <Typography variant="body2" sx={{ opacity: 0.7 }} color="var(--wb-text-strong)">
                        {source.description}
                    </Typography>
                    {source.url && (
                        <Typography variant="body2" sx={{ opacity: 0.7 }} color="var(--wb-text-strong)">
                            {source.url}
                        </Typography>
                    )}
                    {source.connectionType === 'snowflake' && (
                        <Typography variant="body2" sx={{ opacity: 0.7, wordBreak: 'break-all' }} color="var(--wb-text-strong)">
                            {source.authMethod === 'key_pair'
                                ? `Par de claves${source.keyFingerprint ? ` · ${source.keyFingerprint}` : ''}`
                                : 'Contraseña (Snowflake la bloquea: edita la conexión y genera un par de claves)'}
                            {source.warehouse ? ` · Warehouse ${source.warehouse}` : ''}
                            {source.role ? ` · Rol ${source.role}` : ''}
                        </Typography>
                    )}
                    {source.files && (
                        <Typography variant="body2" sx={{ opacity: 0.7 }} color="var(--wb-text-strong)">
                            Archivos ({source.files.length}): {source.files.join(', ')}
                        </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Typography variant="body2" sx={{ mr: 1, opacity: 0.7 }} color="var(--wb-text-strong)">
                            Última sincronización: {source.lastSync}
                        </Typography>
                        {renderStatus(source)}
                        {/* Ingesta terminada con algunas hojas fallidas: la
                            conexión queda activa, pero las que fallaron se
                            siguen pudiendo consultar. */}
                        {!['error', 'processing', 'ingesting'].includes(source.status) && source.sheetsFailed > 0 && (
                            <Typography variant="body2" sx={{ ...errorLinkSx, ml: 1 }} {...errorLinkProps}>
                                {source.sheetsFailed === 1 ? '1 hoja con error' : `${source.sheetsFailed} hojas con error`}
                            </Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Las conexiones de archivo (Excel/CSV) no tienen credenciales
                            ni host que "probar" -- connection_tester.py no tiene rama
                            para connection_type 'excel'/'csv' y siempre devuelve
                            "Unsupported database type", lo que hacía que este botón
                            pisara el estado real (p. ej. 'active' con ingesta OK) con
                            un 'error' falso solo en el estado local de React. */}
                        {source.type !== 'Excel' && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => onTest(source.id)}
                                disabled={connectingSourceIds.includes(source.id)}
                                sx={{
                                    borderColor: 'text.secondary',
                                    color: connectingSourceIds.includes(source.id) ? 'var(--wb-text-dim)' : 'white'
                                }}
                            >
                                {connectingSourceIds.includes(source.id) ? 'Probando...' : 'Probar'}
                            </Button>
                        )}
                        {canModify ? (
                            <>
                                <IconButton
                                    size="small"
                                    onClick={() => onEdit(source.id)}
                                    sx={{ color: 'var(--wb-text-strong)', ":hover": { backgroundColor: 'var(--wb-overlay-08)' }, transition: 'color 0.2s' }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => onDelete(source.id)}
                                    sx={{ color: 'var(--wb-text-strong)', ":hover": { color: 'red', backgroundColor: 'var(--wb-overlay-08)' }, transition: 'color 0.2s' }}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </>
                        ) : (
                            <IconButton
                                size="small"
                                onClick={() => onView(source.id)}
                                sx={{ color: 'var(--wb-primary)', ":hover": { backgroundColor: 'var(--wb-x-bg-0095ff14, rgba(0, 149, 255, 0.08))' }, transition: 'color 0.2s' }}
                                title="Ver detalles"
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            </Grid>
            <SheetErrorsDialog
                open={errorDialogOpen}
                onClose={() => setErrorDialogOpen(false)}
                source={source}
            />
        </Paper>
    );
};

export default DataSourceCard;
